import { QK_EVENT_TYPES } from '@/queries/eventTypes.queries';
import IEventType from '@/types/IEventType';
import IQuestion from '@/types/IQuestion';
import generateSlug from '@/utils/generateSlug';
import { useIsMutating } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface IUseSectionQuestionsArgs {
	service: IEventType;
	questions: Array<IQuestion>;
	onChange: (values: Partial<IEventType>) => void;
}

export default function useSectionQuestions(args: IUseSectionQuestionsArgs) {
	const { t } = useTranslation();
	const isMutating = useIsMutating({ mutationKey: [QK_EVENT_TYPES] }) > 0;

	const privacyForm = useForm({
		defaultValues: {
			isEnabled: !!args.service.tags.ttGDPRMode,
			privacyLink: args.service.tags.ttGDPRMode || '',
		},
	});

	function handleQuestionChange(question: IQuestion) {
		const questions = [...args.questions];
		const index = args.questions.findIndex((q) => q.id === question.id) as number;

		questions[index] = question;

		args.onChange({ questions });
	}

	function handleAddQuestionClick() {
		const questions = [...args.questions];

		questions.push({
			id: `${generateSlug()}`,
			label: t('New question'),
			required: false,
			type: 'TEXT',
		});

		args.onChange({
			questions,
		});
	}

	function handleDeleteClick(question: { id: string }) {
		const questions = args.questions.filter((q) => q.id !== question.id);

		args.onChange({
			questions,
		});
	}

	function handleGDPRChange(e: React.ChangeEvent<HTMLInputElement>) {
		const checked = e.target.checked;

		args.onChange({
			tags: {
				...args.service.tags,
				ttGDPRMode: checked ? 'enabled' : undefined,
			},
		});
	}

	function handlePrivacyBlur(e: React.ChangeEvent<HTMLInputElement>) {
		const privacyLink = e.target.value;

		args.onChange({
			tags: {
				...args.service.tags,
				ttGDPRMode: privacyLink,
			},
		});
	}

	function handlePrivacyEnabledChange(e: React.ChangeEvent<HTMLInputElement>) {
		const value = e.target.checked;
		privacyForm.setValue('isEnabled', value);

		if (!value) {
			privacyForm.setValue('privacyLink', '');

			args.onChange({
				tags: { ...args.service.tags, ttGDPRMode: '' },
			});
		}
	}

	return {
		handlePrivacyEnabledChange,
		handleAddQuestionClick,
		handleDeleteClick,
		handleQuestionChange,
		handleGDPRChange,
		handlePrivacyBlur,
		privacyForm,
		t,
		isLoading: isMutating,
		gdprLink: !!args.service.tags.ttGDPRMode,
		isGDPREnabled: !!args.service.tags.ttGDPRMode,
	};
}
